import { useMemo, useEffect, useContext } from 'react'
import * as React from 'react'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Divider from 'LuxKit/Divider'
import FlightCartReviewTileExtension from 'components/Pages/CartReviewPage/FlightCartReviewTileExtension'
import { FlightViewTypes } from 'constants/flight'
import styled from 'styled-components'
import useRefreshMultiCartFlightItem from '../hooks/useRefreshMultiCartFlightItem'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { buildStandaloneFlightSearchUrl, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getFlightsTotal } from 'lib/flights/flightUtils'
import ExpiryTimer from 'components/Pages/CartReviewPage/ExpiryTimer'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import { arrayToMap, sortBy } from 'lib/array/arrayUtils'
import { isTwoOneWaysItem } from 'components/Pages/CartReviewPage/CartReviewUtils'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { dateIsBefore, intlDateFormatter, startOfDay } from 'lib/datetime/dateUtils'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import getLoyaltyEarnKeyFromFlightItems from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems'
import SharedCartComments from '../Comments/SharedCartComments'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEAdmin } from 'selectors/accountSelectors'
import MaldivesBundleDiscountBadge from 'components/Common/Labels/MaldivesBundleDiscountBadge'
import { useMaldivesBundleRefreshContext } from '../hooks/MaldivesBundleRefreshContext'
import { toAutoDeleteCandidates, toCurrentStates } from '../multiCartUtils'
import useCharterRewardAvailability from 'hooks/Flights/useCharterRewardAvailability'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'

const ExtensionContainer = styled(Group)`
  grid-area: extension;
`

const DividerPadding = styled.div`
  padding-inline: ${rem(20)};
`

interface Props {
  multiCartItem: App.MultiCartFlightItem
  tileVariant?: 'default' | 'condensed'
  hideActionButtons?: boolean
  hidePrices?: boolean
  hidePassengerCount?: boolean
  hideLoyaltyPoints?: boolean
  autoDeleteItemIds?: Array<string>
  onAutoDeleteUnavailable?: (itemId: string) => void
}

function FlightMultiCartReviewTile({ multiCartItem, tileVariant = 'default', hideActionButtons = false, hidePrices = false, hidePassengerCount = false, hideLoyaltyPoints = false, autoDeleteItemIds, onAutoDeleteUnavailable }: Props) {
  const intl = useIntl()
  const item = multiCartItem.item as App.Checkout.FlightItem

  // Check if this flight is eligible for bundle pricing and being auto-refreshed
  const { refreshingFlightIds, eligibleFlightIds } = useMaldivesBundleRefreshContext()
  const isBundleEligible = eligibleFlightIds.has(item.itemId)
  const isBundleRefreshing = refreshingFlightIds.has(item.itemId)

  // Pass bundle eligibility to refresh hook so expired flights refresh with correct pricing context
  const { dataStatus, initiateRefresh } = useRefreshMultiCartFlightItem(multiCartItem, { isBundleEligible })

  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type !== 'default'
  const showComments = !hideActionButtons && isSharedCart

  // Maldives bundle discount: flight is using bundle pricing (no 15% markup)
  const isBundlePriced = multiCartItem.isBundlePriced ?? false

  const activeFlightCartItems = cartItemsState.status === 'loaded' && activeCartId ? Object.values(cartItemsState.data[activeCartId] || {})
    .filter(multiCartItem => isFlightItem(multiCartItem.item) && multiCartItem.state === 'active') : []

  const departingDate = item.flights[0].departingDate
  const isPast = !!departingDate && dateIsBefore(getLocalDate(departingDate), startOfDay(new Date()))

  const failedRefresh = dataStatus === 'refreshFailed'
  const hasExpired = dataStatus === 'expired'
  const isAvailable = !failedRefresh && !hasExpired && !isPast
  const canAutoDeleteUnavailableFlight = hasExpired || isPast
  const isBookable = multiCartItem.state !== 'deleted' && isAvailable
  const canShowExtensionAndTimer = !!multiCartItem.expiry && dataStatus !== 'refreshFailed'
  const isSavedForLater = multiCartItem.state === 'save_for_later'

  const flightSearchUrl = useMemo(() => buildStandaloneFlightSearchUrl({
    originAirportCode: multiCartItem.originAirportCode,
    originAirportName: multiCartItem.flights[0].departingAirportName,
    destinationAirportCode: multiCartItem.destinationAirportCode,
    destinationAirportName: multiCartItem.flights[0].arrivalAirportName,
    departDate: multiCartItem.flights[0].departingDate,
    returnDate: multiCartItem.flights.length > 1 ? multiCartItem.flights[1].departingDate : null,
    adults: multiCartItem.occupants.adults,
    children: multiCartItem.occupants.children ?? 0,
    infants: multiCartItem.occupants.infants ?? 0,
    childrenAge: multiCartItem.occupants.childrenAge?.join(',') ?? '',
    fareClass: multiCartItem.flights[0].fareFamily?.cabin?.toLowerCase() ?? 'economy',
    fareType: multiCartItem.fareType,
  }), [
    multiCartItem.originAirportCode,
    multiCartItem.flights,
    multiCartItem.destinationAirportCode,
    multiCartItem.occupants,
    multiCartItem.fareType,
  ])

  const { airports } = useContext(GeoContext)

  const hasTwoOneWaysFlightsInUserCart = () => {
    if (!activeFlightCartItems.length) return false

    const twoOneWaysFlights = activeFlightCartItems.find(item => (item.item as App.Checkout.FlightItem).viewType === FlightViewTypes.TWO_ONE_WAYS)
    if (!twoOneWaysFlights) return false

    return isTwoOneWaysItem(twoOneWaysFlights.item as App.Checkout.FlightItem)
  }

  const isTwoOneWaysAndUserCartContainsFlights = () => {
    return activeFlightCartItems.length && isTwoOneWaysItem(item)
  }

  useEffect(() => {
    // Refresh failures are recoverable; only explicit expired or past flights trigger auto-delete.
    const flightIsNoLongerSellable = canAutoDeleteUnavailableFlight
    // Only active items can be auto-deleted; saved/deleted states are owned by user or machine actions.
    const flightItemCanBeDeleted = multiCartItem.state === 'active'
    // Standalone tiles need the cart id to send the delete event themselves.
    const activeCartIsAvailable = !!activeCartId
    const shouldAutoDeleteFlight = flightIsNoLongerSellable && flightItemCanBeDeleted && activeCartIsAvailable

    if (!shouldAutoDeleteFlight) {
      return
    }

    if (onAutoDeleteUnavailable) {
      onAutoDeleteUnavailable(multiCartItem.id)
      return
    }

    if (!activeCartId) {
      return
    }

    multiCartActorRef.send({
      type: 'auto delete unavailable item candidates',
      cartId: activeCartId,
      candidates: toAutoDeleteCandidates(autoDeleteItemIds ?? [multiCartItem.id]),
    })
  }, [canAutoDeleteUnavailableFlight, multiCartItem.state, activeCartId, multiCartItem.id, onAutoDeleteUnavailable, multiCartActorRef, autoDeleteItemIds])

  const formattedOccupancy = useMemo(() => {
    if (hidePassengerCount) return ''

    const hasInfants = multiCartItem.occupants.infants && multiCartItem.occupants.infants > 0
    const formattedOccupants = formatOccupantsShort(multiCartItem.occupants)
    if (hasInfants) {
      return intl.formatMessage(
        {
          id: 'Fx_ROe5JMxjZplam8VXk',
          defaultMessage: '{occupants} (on lap)',
          description: 'Flight occupancy summary with infant on lap indicator in cart review tile',
        },
        { occupants: formattedOccupants },
      )
    }
    return formattedOccupants
  }, [multiCartItem.occupants, hidePassengerCount, intl])

  const title = useMemo(() => {
    switch (multiCartItem.viewType) {
      case FlightViewTypes.ONE_WAY:
        return intl.formatMessage({
          id: 'HicELZ331FBHEbnOm3WE',
          defaultMessage: 'One-way flight',
          description: 'Title for one-way flight item in the multi-cart review tile',
        })
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
        return intl.formatMessage({
          id: 'tRaKUBZvziM0lZjFphjB',
          defaultMessage: 'Return flights',
          description: 'Title for return flights item in the multi-cart review tile',
        })
      case FlightViewTypes.MULTI_CITY:
        return intl.formatMessage({
          id: 'f2WKL4fniyx-hStpOoj7',
          defaultMessage: 'Multi-city flights',
          description: 'Title for multi-city flights item in the multi-cart review tile',
        })
      default:
        return ''
    }
  }, [multiCartItem.viewType, intl])

  const dates = useMemo(() => {
    const firstFlight = multiCartItem.flights[0]
    if (!firstFlight.departingDate || !firstFlight.departingAirportName || !firstFlight.arrivalAirportName) {
      return ''
    }

    const date = intlDateFormatter(firstFlight.departingDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)

    switch (multiCartItem.viewType) {
      case FlightViewTypes.ONE_WAY:
        return intl.formatMessage({
          id: '6DNWWEdp--Jolu13faS5',
          defaultMessage: '{date} · {departingAirport} to {arrivalAirport}',
          description: 'One-way flight summary showing date, departure and arrival airports',
        }, { date, departingAirport: firstFlight.departingAirportName, arrivalAirport: firstFlight.arrivalAirportName })
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
      case FlightViewTypes.MULTI_CITY: {
        const lastFlight = multiCartItem.flights[multiCartItem.flights.length - 1]
        if (!lastFlight.departingDate || !lastFlight.arrivalAirportName) {
          return ''
        }

        const endDate = intlDateFormatter(lastFlight.departingDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)
        const fromCity = firstFlight.departingAirportName
        const toCity = multiCartItem.viewType === FlightViewTypes.MULTI_CITY ? lastFlight.arrivalAirportName : firstFlight.arrivalAirportName

        return intl.formatMessage({
          id: 'myZf8AXBjGfIbm_BuqHR',
          defaultMessage: '{startDate} - {endDate} · {fromCity} to {toCity}',
          description: 'Multi-leg flight summary showing date range, departure and arrival cities',
        }, { startDate: date, endDate, fromCity, toCity })
      }
      default:
        return ''
    }
  }, [multiCartItem.flights, multiCartItem.viewType, intl])

  const totalCost = useMemo(() => getFlightsTotal(item), [item])

  const isReward = !!item.isClassicReward
  const rewardPassengerCount = useMemo(
    () => item.passengers.filter(p => p.type !== 'infant').length,
    [item.passengers],
  )
  const rewardLegs = useMemo(
    () => item.flights.map(f => ({
      journeyKey: f.rewardJourneyKey ?? f.journeyKey,
      legTotal: f.cost ?? 0,
    })),
    [item.flights],
  )
  const {
    available: rewardAvailable,
    pointsPerPerson: rewardPointsPerPerson,
    cashCoPayPerPerson: rewardCashCoPayPerPerson,
  } = useCharterRewardAvailability({
    legs: rewardLegs,
    passengerCount: rewardPassengerCount,
    disabled: !isReward || rewardLegs.length === 0,
  })
  const rewardTotalPoints = (rewardPointsPerPerson ?? 0) * rewardPassengerCount
  const rewardTotalCash = (rewardCashCoPayPerPerson ?? 0) * rewardPassengerCount

  const bannerLabelVariant = () => {
    if (isPast) return 'pastDates'
    if (hasExpired) return 'expired'
    if (failedRefresh) return 'failedRefresh'
    if (isBookable && multiCartItem.state === 'save_for_later' && hasTwoOneWaysFlightsInUserCart()) return 'bookingLimit'
    if (multiCartItem.state === 'save_for_later' && isTwoOneWaysAndUserCartContainsFlights()) return 'bookingLimit'
    return null
  }

  const bannerContent = () => {
    switch (bannerLabelVariant()) {
      case 'pastDates':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'XUmx0kwrYLkLaqaM4XCG',
              defaultMessage: 'Your selected dates are in the past.',
              description: 'Warning message when flight dates in cart are past dates',
            })}
            </BodyText>
            <TextLink weight="bold" to="/flights" target="_blank">
              {intl.formatMessage({
                id: 'r2nHQHRc0vJ01JsHbomu',
                defaultMessage: 'Check availability',
                description: 'Hyperlink text to check availability in the shopping cart',
              })}
            </TextLink>
          </Group>
        )
      case 'expired':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'ZDZaT5fohIrcwwKdI0gJ',
              defaultMessage: 'This flight price has expired',
              description: 'Warning notification message when flight price has expired',
            })}
            </BodyText>
            <TextLink weight="bold" onClick={initiateRefresh}>
              {intl.formatMessage({
                id: '7Sxljrd5uHOal_uyr_f1',
                defaultMessage: 'Refresh search',
                description: 'Link text to refresh flight search in the flight search or booking flow',
              })}
            </TextLink>
          </Group>
        )
      case 'failedRefresh':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'RenayifZewRDwd0ZNIjC',
              defaultMessage: 'We couldn’t refresh your flight.',
              description: 'Error message when flight price refresh fails in cart',
            })}
            </BodyText>
            <TextLink weight="bold" to={flightSearchUrl} target="_blank">
              {intl.formatMessage({
                id: '_6pF-iGMJyiSSZNkN_WP',
                defaultMessage: 'Search for flights',
                description: 'Link text to search for flights in the flight search or booking flow',
              })}
            </TextLink>
          </Group>
        )
      case 'bookingLimit':
        return intl.formatMessage({
          id: 'Osk_Hc7c5G3GZx14hgkX',
          defaultMessage: 'This flight needs to be booked separately from your current selection',
          description: 'Warning message when flight needs to be booked separately from current selection',
        })
      default:
        return ''
    }
  }

  const banner = () => {
    if (!bannerLabelVariant()) return null

    return (
      <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
        {bannerContent()}
      </MessageBanner>
    )
  }

  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // TODO: How  to test for hasTwoOneWaysFlightsInUserCart() || isTwoOneWaysItem(item)
    // this tile is already saved for later, so user is +adding them back, so bring back all other flights which are heading in same direction.
    if (isSavedForLater) {
      const flightCartItemIdsToSaveForLater = (isSavedForLater && (hasTwoOneWaysFlightsInUserCart() || isTwoOneWaysItem(item))) ?
          activeFlightCartItems
            .map(flightCartItem => flightCartItem.id)
        : []
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [...flightCartItemIdsToSaveForLater, multiCartItem.id],
        saveForLater: !(multiCartItem.state === 'save_for_later'),
      })
    } else {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: !(multiCartItem.state === 'save_for_later'),
      })
    }
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates: toCurrentStates([multiCartItem]),
    })
  }

  const extension = useMemo(() => {
    if (!canShowExtensionAndTimer) return null

    return (
      <ExtensionContainer direction="vertical" gap={8}>
        <DividerPadding><Divider kind="primary" /></DividerPadding>
        {multiCartItem.flights.map((flight, index) => (
          <React.Fragment key={flight.journeyId}>
            <FlightCartReviewTileExtension flight={flight} isClassicReward={isReward} />
            {index < multiCartItem.flights.length - 1 && <DividerPadding><Divider kind="primary" /></DividerPadding>}
          </React.Fragment>
        ))}
      </ExtensionContainer>
    )
  }, [canShowExtensionAndTimer, multiCartItem.flights, isReward])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    // Classic Rewards flight items aren't earnable — points cover the flight and the
    // cash co-pay is excluded. Return empty calculation requests so no earn line renders
    // on the cart-review flight tile.
    if (isReward) {
      return { calculationRequests: [], calculationType: 'exact' }
    }
    const airportsByCode = arrayToMap(airports, airport => airport.code)

    const loyaltyProductTypes = multiCartItem.flights.map(flight => {
      const departingAirport = airportsByCode.get(flight.departingAirportCode)
      const arrivalAirport = airportsByCode.get(flight.arrivalAirportCode)

      const isDomestic = departingAirport?.countryCode === arrivalAirport?.countryCode
      return getFlightLoyaltyProductType(flight.fareFamily?.cabin?.toLowerCase() ?? 'economy', isDomestic)
    })

    const rankedLoyaltyProductTypes = sortBy(loyaltyProductTypes, productType => getFlightLoyaltyProductTypeRank(productType), 'asc')
    const highestRankedProductType = rankedLoyaltyProductTypes[0]

    const earnKey = getLoyaltyEarnKeyFromFlightItems(item.flights, highestRankedProductType, airports)

    return {
      calculationRequests: [{
        loyaltyProductType: highestRankedProductType,
        price: totalCost,
        earnKey,
        offerId: '',
        packageId: '',
      }],
      calculationType: 'exact',
    }
  }, [airports, multiCartItem.flights, totalCost, item.flights, isReward])

  if (dataStatus === 'refreshing' || isBundleRefreshing) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (multiCartItem.state === 'deleted') {
    return <UndoDeletedItemsTile
      title={title}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    const condensedPrice = isReward && rewardAvailable ? <Group direction="vertical" horizontalAlign="end" gap={2}>
      <Group direction="horizontal" gap={4} verticalAlign="baseline">
        <Heading variant="heading6">
          <FormattedMessage
            id="RjPYIEbS2Da3ihN-THCJ"
            defaultMessage="{points} + {cash}"
            description="Reward flight price in the condensed cart review tile showing loyalty points plus cash co-pay"
            values={{
              points: <FormatLoyaltyPoints points={rewardTotalPoints} abbreviated />,
              cash: <FormatCurrency value={rewardTotalCash} format="roundedDollar" />,
            }}
          />
        </Heading>
      </Group>
    </Group> : <CartReviewPriceStack
      price={totalCost}
      value={totalCost}
    />

    return (
      <CondensedCartReviewTile
        banner={banner()}
        content={<CartReviewTileContent
          title={title}
          dates={dates}
          occupancy={formattedOccupancy}
          loyaltyPointsEarningConfiguration={hideLoyaltyPoints ? undefined : loyaltyPointsEarningConfiguration}
          label={<ExpiryTimer
            canShowExtension={canShowExtensionAndTimer}
            expiry={multiCartItem.expiry ?? 0}
          />}
        />}
        onDelete={hideActionButtons ? undefined : handleDelete}
        price={condensedPrice}
      />
    )
  }

  const actions = !hideActionButtons ? <CartReviewTileActions
    isBookable={isBookable}
    saveForLater={isSavedForLater}
    onSaveForLater={handleSaveForLater}
    onDelete={handleDelete}
  /> : undefined

  const price = !hidePrices ? (isReward && rewardAvailable ? <Group direction="vertical" horizontalAlign="end" gap={2}>
    <Group direction="horizontal" gap={4} verticalAlign="baseline">
      <Heading variant="heading6">
        <FormattedMessage
          id="EUug-qNEAyg1W8nopc5Y"
          defaultMessage="{points} + {cash}"
          description="Reward flight price in the cart review tile showing loyalty points plus cash co-pay"
          values={{
            points: <FormatLoyaltyPoints points={rewardTotalPoints} abbreviated />,
            cash: <FormatCurrency value={rewardTotalCash} format="roundedDollar" />,
          }}
        />
      </Heading>
    </Group>
    <Caption variant="medium" colour="neutral-three">
      <FormattedMessage
        id="4KMnOJnbkwyq1BVr29ae"
        defaultMessage="Reward flight"
        description="Label below the points + cash price in the cart review tile indicating this is a Classic Rewards flight"
      />
    </Caption>
  </Group> : <CartReviewPriceStack
    price={totalCost}
    value={totalCost}
  />) : null

  const commentsFooter = showComments && activeCartId ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      isAdmin={isAdmin}
      noBorder
    />
  ) : undefined

  return (
    <CartReviewTile
      content={<CartReviewTileContent
        title={title}
        dates={dates}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        label={<>
          <ExpiryTimer
            canShowExtension={canShowExtensionAndTimer}
            expiry={multiCartItem.expiry ?? 0}
          />
          <MultiCartParticipantLabel item={multiCartItem} />
          {isBundlePriced && <MaldivesBundleDiscountBadge />}
        </>}
      />}
      price={price}
      banner={banner()}
      extension={extension}
      footer={commentsFooter}
      isBookable={isBookable}
      saveForLater={isSavedForLater}
      actions={actions}
    />
  )
}

export default FlightMultiCartReviewTile
